import type { ReactNode } from "react";

/**
 * A sidebar card.
 *
 * Fields inside stay label-above-value: the sidebar column is ~240px, and
 * label-left/value-right there truncates the values that matter. Wide surfaces
 * (overlays, the support sheet) use `OverlaySection` and put them on one line.
 */
export function SidebarSection({
  title,
  onViewAll,
  children,
}: {
  title: string;
  /** Adds a "View all" button opening the section's full-detail overlay. */
  onViewAll?: () => void;
  children: ReactNode;
}) {
  return (
    <section className="bg-card rounded-lg p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="text-foreground text-sm font-semibold">{title}</h3>
        {onViewAll && (
          <button
            type="button"
            onClick={onViewAll}
            className="border-border text-foreground hover:border-muted-foreground/40 hover:bg-muted rounded-md border px-2.5 py-1 text-xs font-medium shadow-xs transition-colors focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none"
          >
            View all
          </button>
        )}
      </div>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

/** Label above value. The sidebar's field shape. */
export function SidebarField({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div>
      <p className="text-muted-foreground text-sm">{label}</p>
      <div className="text-foreground text-sm">{children}</div>
    </div>
  );
}
